/**
=========================================================
* Material Dashboard 2 PRO React - v2.1.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-pro-react
* Copyright 2022 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/
import { useTranslation } from "react-i18next";

import { useState, useEffect } from "react";

// @mui material components
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import Switch from "@mui/material/Switch";
import Tooltip from "@mui/material/Tooltip";
import Divider from "@mui/material/Divider";

// Material Dashboard 2 PRO React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDAvatar from "components/MDAvatar";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";
import { useKindeAuth } from "@kinde-oss/kinde-auth-react";

// Images
import logoSlack from "assets/images/small-logos/logo-slack.svg";
import logoSpotify from "assets/images/small-logos/logo-spotify.svg";
import logoAtlassian from "assets/images/small-logos/logo-atlassian.svg";
import logoAsana from "assets/images/small-logos/logo-asana.svg";

// Material Dashboard 2 PRO React components
import { useMaterialUIController } from "context";
import { useApiClient } from "common/ApiClientProvider";

function Optout() {
  const { t } = useTranslation("pages");
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;
  const { getUser } = useKindeAuth();
  const apiClient = useApiClient();
  const user = getUser();

  const [improve, setImprove] = useState(true);

  //const handleSetImprove = () => setImprove(!improve);
  async function getImproveModel() {
    const improveModel = await apiClient.get(`improve_models/${encodeURIComponent(user.id)}/`);
    setImprove(improveModel.improve ?? true);
  }

  async function saveImproveModel() {
    const currentStatus = improve;
    setImprove(!currentStatus);
    try {
      await apiClient.put(`improve_models/${encodeURIComponent(user.id)}/`, {
        body: {
          improve: !currentStatus,
        },
      });
    } catch (error) {
      setImprove(currentStatus);
      throw error;
    }
  }

  useEffect(() => {
    getImproveModel();
  }, []);

  return (
    <Card id="opt-out">
      <MDBox p={3} lineHeight={1}>
        <MDBox mb={1}>
          <MDTypography variant="h5">{t("accountSettings.optOut")}</MDTypography>
        </MDBox>
        <MDTypography variant="button" color="text">
          {t("accountSettings.optOutDescription")}
        </MDTypography>
      </MDBox>
      <MDBox pt={2} pb={3} px={3}>
        <MDBox
          display="flex"
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", sm: "center" }}
          flexDirection={{ xs: "column", sm: "row" }}
        >
          <MDBox display="flex" alignItems="center">
            <MDBox ml={2} lineHeight={0}>
              <MDTypography variant="h5" fontWeight="medium">
                {t("accountSettings.improveModel")}
              </MDTypography>
              <MDTypography variant="button" color="text">
                Allow your content to be used to train our models, which makes our services better
                for you and everyone who uses it. We take steps to protect your privacy.
              </MDTypography>
            </MDBox>
          </MDBox>
          <MDBox
            display="flex"
            justifyContent="flex-end"
            alignItems="center"
            width={{ xs: "100%", sm: "auto" }}
            mt={{ xs: 1, sm: 0 }}
          >
            <MDBox lineHeight={0} mx={2}>
              <MDTypography variant="button" color="text">
                {improve ? t("accountUi.enabled") : t("accountUi.disabled")}
              </MDTypography>
            </MDBox>
            <MDBox mr={1}>
              <Switch checked={improve} onChange={saveImproveModel} />
            </MDBox>
          </MDBox>
        </MDBox>
      </MDBox>
    </Card>
  );
}

export default Optout;
